import {
	AppCategory,
	AppColorViewModel,
	AppType
} from "client/Api/Apps/common";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { environmentHttpClient } from "client/Api/httpClient";
import { SpaceKind, TileGroupType } from "client/Api/Workspace/workspace";
import {
	EditAppModel,
	EditGenericAppViewModel
} from "../EditApp/saveApp";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { TagViewModel } from "client/Api/Tags/tags";

export type NewAppModel = {
	appType: AppType;
	name: string;
	categoryId: number;
	userLicense: UserLicense;
	imageDisplayInfo: AppImageDisplayInfo;
	color: AppColorViewModel;
};

export interface AppAppearanceModel {
	name: string;
	imageDisplayInfo: AppImageDisplayInfo;
	color: AppColorViewModel;
	hasMaintenanceConfigured: boolean;
	hasConditionalAccessConfigured: boolean;
	hasOwnerPermissionsConfigured: boolean;
	tags: readonly TagViewModel[];
}

export type AppStoreModel = {
	apps: readonly AppModel[];
	newApps: readonly NewAppModel[] | undefined;
};

export type RequestAppClientModel = {
	id: number;
	name: string;
	categoryId: number;
	imageDisplayInfo: AppImageDisplayInfo;
	color: AppColorViewModel;
	hasPendingRequest: boolean;
	tags: readonly TagViewModel[];
};

export type SpaceListItem = {
	id: number;
	kind: SpaceKind;
	creatableTileGroupKinds: readonly TileGroupType[];
};

export type AppStorePermissionsClientModel = {
	canRequestAppAccess: boolean;
	canCreateApps: boolean;
	canEditAtleastOneApp: boolean;
	editableSpaces: readonly SpaceListItem[];
};

export function getAppStorePermissions(signal?: AbortSignal): Promise<AppStorePermissionsClientModel> {
	return environmentHttpClient.get<AppStorePermissionsClientModel>(
		"AppStore/Permissions",
		null,
		{ signal }
	);
}

export function getAppStoreModel(signal?: AbortSignal): Promise<AppStoreModel> {
	return environmentHttpClient.get<AppStoreModel>(
		"AppStore/Apps",
		null,
		{ signal }
	);
}

export function enableApp(mainAppId: number, signal?: AbortSignal): Promise<number> {
	return environmentHttpClient.post<number>(
		`AppStore/Enable/${mainAppId}`,
		undefined,
		{ signal }
	);
}

export function disableApp(installedAppId: number, signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.put<void>(
		`AppStore/Disable/${installedAppId}`,
		undefined,
		{ signal }
	);
}

function getEditAppModel(
	installedAppId: number,
	signal?: AbortSignal
): Promise<EditAppModel<EditGenericAppViewModel>> {
	return environmentHttpClient.get<EditAppModel<EditGenericAppViewModel>>(
		`AppStore/EditApp/${installedAppId}`,
		undefined,
		{ signal }
	);
}

function getNewEnvironmentAppModel(
	appType: AppType,
	signal?: AbortSignal
): Promise<EditAppModel<EditGenericAppViewModel>> {
	return environmentHttpClient.get<EditAppModel<EditGenericAppViewModel>>(
		`AppStore/NewApp/${appType}`,
		undefined,
		{ signal }
	);
}

function getNewAppFromMainModel(
	mainAppId: number,
	signal?: AbortSignal
): Promise<EditAppModel<EditGenericAppViewModel>> {
	return environmentHttpClient.get<EditAppModel<EditGenericAppViewModel>>(
		`AppStore/NewAppFromMain/${mainAppId}`,
		undefined,
		{ signal }
	);
}

export function getAppAppearance(installedAppId: number): Promise<AppAppearanceModel> {
	return environmentHttpClient.get<AppAppearanceModel>(
		`AppStore/AppAppearance/${installedAppId}`
	);
}

export function getMainAppAppearance(mainAppId: number): Promise<AppAppearanceModel> {
	return environmentHttpClient.get<AppAppearanceModel>(
		`AppStore/MainAppAppearance/${mainAppId}`
	);
}

export function getTileInstanceCount(installedAppId: number): Promise<number> {
	return environmentHttpClient.get<number>(
		`AppStore/GetTileInstanceCount/${installedAppId}`
	);
}

export function getRequestAppModels(signal: AbortSignal): Promise<readonly RequestAppClientModel[]> {
	return environmentHttpClient.get<readonly RequestAppClientModel[]>(
		"AppStore/GetRequestAppModels",
		null,
		{ signal }
	);
}

export {
	getEditAppModel,
	getNewEnvironmentAppModel,
	getNewAppFromMainModel
};

export interface AppModel {
	installedAppId: number | null;
	mainAppId: number;
	appType: AppType;
	name: string;
	categoryId: AppCategory;
	userLicense: UserLicense;
	imageDisplayInfo: AppImageDisplayInfo;
	color: AppColorViewModel;
	canUserEditApp: boolean;
	canUserDisableApp: boolean;
	isGoneAfterDisabling: boolean;
	isFeaturedAsNew: boolean;
	hasMaintenanceConfigured: boolean;
	hasConditionalAccessConfigured: boolean;
	hasOwnerPermissionsConfigured: boolean;
	tags: readonly TagViewModel[];
}
